import { useDtoolsPdfPackageMode } from "@/components/sales/dtools/DtoolsPdfPackageContext";
import { useDtoolsProposalDisplay } from "@/components/sales/dtools/DtoolsProposalDisplayContext";
import { PROPOSAL_ACCENT, PROPOSAL_COPY_FONT, PROPOSAL_TEXT } from "@/lib/proposalTheme";
import { PDF_BODY_CLASS, PDF_EYEBROW_CLASS } from "@/lib/proposalPdfTypography";
import type { DtoolsReviewSystemView } from "@shared/dtoolsReviewNavigation";
import { renderVoOverviewAnnotation } from "@shared/rendering";

type Props = {
  view: DtoolsReviewSystemView;
};

export default function DtoolsReviewOverviewPanel({ view }: Props) {
  const { overview } = view;
  const pdfPackageMode = useDtoolsPdfPackageMode();
  const display = useDtoolsProposalDisplay();
  const annotation = renderVoOverviewAnnotation(overview.variationOrderAnnotation, {
    staffLinks: display.audience === "staff",
  });

  return (
    <div
      className={
        pdfPackageMode
          ? "space-y-1.5 rounded-lg border border-[#1A2744]/10 bg-white p-3"
          : "space-y-5 rounded-xl border border-[#1A2744]/10 bg-[#FAFAF7]/80 p-5 sm:p-6"
      }
      style={{ fontFamily: PROPOSAL_COPY_FONT }}
      data-testid="dtools-system-overview-panel"
    >
      <div>
        <p className={pdfPackageMode ? PDF_EYEBROW_CLASS : "text-xs font-medium uppercase tracking-[0.16em]"} style={{ color: PROPOSAL_ACCENT }}>
          Executive Overview
        </p>
        {annotation ? (
          <div className="mt-2 flex flex-wrap items-center gap-2" data-testid="vo-overview-annotation">
            <span className="inline-flex rounded border border-[#C8A96A]/70 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-[#1A2744]">
              {annotation.statusBadge}
            </span>
            <span className="text-xs font-medium text-[#1A2744]">{annotation.badge}</span>
            {annotation.metaLine ? (
              <span className="text-xs text-muted-foreground">· {annotation.metaLine}</span>
            ) : null}
          </div>
        ) : null}
        <p
          className={
            pdfPackageMode
              ? `mt-1.5 whitespace-pre-wrap ${PDF_BODY_CLASS}`
              : "mt-2 whitespace-pre-wrap text-sm leading-7 text-[#374151]"
          }
          style={pdfPackageMode ? undefined : { color: `${PROPOSAL_TEXT}E6` }}
        >
          {overview.description}
        </p>
        {annotation?.viewHref ? (
          <p className="mt-2 text-xs">
            <a
              href={annotation.viewHref}
              className="font-medium text-[#1A2744] underline-offset-2 hover:underline"
              data-testid="vo-overview-view-link"
            >
              View Variation Order
            </a>
          </p>
        ) : null}
      </div>
    </div>
  );
}
